"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const lucide_react_1 = require("lucide-react");
const api_1 = require("../services/api");
const SubtitleSourceManager = ({ projectId, onSourceSelect, onSourceComparison, selectedSourceId, onSourcesChange }) => {
    const [sources, setSources] = (0, react_1.useState)([]);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    const [creatingSource, setCreatingSource] = (0, react_1.useState)(null);
    (0, react_1.useEffect)(() => {
        loadSources();
    }, [projectId]);
    const loadSources = async () => {
        try {
            setLoading(true);
            const response = await api_1.projectsApi.getProjectSubtitleSources(projectId);
            setSources(response.sources);
            setError(null);
        }
        catch (err) {
            console.error('Error loading sources:', err);
            setError('Chyba při načítání zdrojů titulků');
        }
        finally {
            setLoading(false);
        }
    };
    const createYouTubeSource = async (language = 'cs') => {
        try {
            setCreatingSource('youtube');
            await api_1.projectsApi.createYouTubeSubtitleSource(projectId, language);
            await loadSources();
        }
        catch (err) {
            console.error('Error creating YouTube source:', err);
            setError('Chyba při vytváření YouTube zdroje');
        }
        finally {
            setCreatingSource(null);
        }
    };
    const createWhisperSource = async (audioFilePath) => {
        try {
            setCreatingSource('whisper');
            await api_1.projectsApi.createWhisperSubtitleSource(projectId, audioFilePath);
            await loadSources();
            // Po vytvoření Whisper zdroje refreshneme aktivní projekt, aby se segmenty načetly
            if (onSourcesChange) {
                onSourcesChange();
            }
        }
        catch (err) {
            console.error('Error creating Whisper source:', err);
            setError('Chyba při vytváření Whisper zdroje');
        }
        finally {
            setCreatingSource(null);
        }
    };
    const deleteSource = async (sourceId) => {
        if (!confirm('Opravdu chcete smazat tento zdroj titulků?'))
            return;
        try {
            await api_1.projectsApi.deleteSubtitleSource(projectId, sourceId);
            await loadSources();
        }
        catch (err) {
            console.error('Error deleting source:', err);
            setError('Chyba při mazání zdroje');
        }
    };
    const handleSourceSelect = (source) => {
        onSourceSelect(source.source_id, source.source_type);
    };
    const handleComparison = () => {
        const completedSources = sources.filter(s => s.status === 'completed');
        if (completedSources.length >= 2) {
            onSourceComparison(completedSources);
        }
    };
    const getSourceIcon = (sourceType) => {
        switch (sourceType) {
            case 'youtube_subtitles':
                return (0, jsx_runtime_1.jsx)(lucide_react_1.FileText, { className: "w-4 h-4" });
            case 'whisper_transcription':
                return (0, jsx_runtime_1.jsx)(lucide_react_1.Mic, { className: "w-4 h-4" });
            default:
                return (0, jsx_runtime_1.jsx)(lucide_react_1.FileText, { className: "w-4 h-4" });
        }
    };
    const getStatusIcon = (status) => {
        switch (status) {
            case 'completed':
                return (0, jsx_runtime_1.jsx)(lucide_react_1.CheckCircle, { className: "w-4 h-4 text-green-500" });
            case 'processing':
                return (0, jsx_runtime_1.jsx)(lucide_react_1.Loader2, { className: "w-4 h-4 text-blue-500 animate-spin" });
            case 'error':
                return (0, jsx_runtime_1.jsx)(lucide_react_1.AlertCircle, { className: "w-4 h-4 text-red-500" });
            default:
                return (0, jsx_runtime_1.jsx)(lucide_react_1.AlertCircle, { className: "w-4 h-4 text-gray-500" });
        }
    };
    const getStatusText = (status) => {
        switch (status) {
            case 'completed':
                return 'Dokončeno';
            case 'processing':
                return 'Zpracovává se...';
            case 'error':
                return 'Chyba';
            default:
                return 'Neznámý';
        }
    };
    if (loading && sources.length === 0) {
        return ((0, jsx_runtime_1.jsx)("div", { className: "subtitle-source-manager loading", children: (0, jsx_runtime_1.jsxs)("div", { className: "loading-content", children: [(0, jsx_runtime_1.jsx)(lucide_react_1.Loader2, { className: "w-6 h-6 animate-spin" }), (0, jsx_runtime_1.jsx)("span", { children: "Na\u010D\u00EDt\u00E1n\u00ED zdroj\u016F titulk\u016F..." })] }) }));
    }
    return ((0, jsx_runtime_1.jsxs)("div", { className: "subtitle-source-manager", children: [(0, jsx_runtime_1.jsxs)("div", { className: "source-manager-header", children: [(0, jsx_runtime_1.jsx)("h3", { children: "Zdroje titulk\u016F" }), (0, jsx_runtime_1.jsxs)("div", { className: "source-actions", children: [(0, jsx_runtime_1.jsxs)("button", { className: "btn btn-secondary btn-sm", onClick: () => createYouTubeSource('cs'), disabled: creatingSource === 'youtube', title: "Na\u010D\u00EDst titulky z YouTube", children: [creatingSource === 'youtube' ? ((0, jsx_runtime_1.jsx)(lucide_react_1.Loader2, { className: "w-4 h-4 animate-spin" })) : ((0, jsx_runtime_1.jsx)(lucide_react_1.FileText, { className: "w-4 h-4" })), "YouTube"] }), (0, jsx_runtime_1.jsxs)("button", { className: "btn btn-secondary btn-sm", onClick: () => createWhisperSource(`${projectId}_audio.mp3`), disabled: creatingSource === 'whisper', title: "Vytvo\u0159it transkripci pomoc\u00ED Whisper AI", children: [creatingSource === 'whisper' ? ((0, jsx_runtime_1.jsx)(lucide_react_1.Loader2, { className: "w-4 h-4 animate-spin" })) : ((0, jsx_runtime_1.jsx)(lucide_react_1.Mic, { className: "w-4 h-4" })), "Whisper"] }), (0, jsx_runtime_1.jsxs)("button", { className: "btn btn-secondary btn-sm", onClick: () => { }, disabled: true, title: "Na\u010D\u00EDst titulky ze souboru (p\u0159ipravov\u00E1no)", children: [(0, jsx_runtime_1.jsx)(lucide_react_1.Download, { className: "w-4 h-4" }), "Soubor"] }), sources.filter(s => s.status === 'completed').length >= 2 && ((0, jsx_runtime_1.jsxs)("button", { className: "btn btn-primary btn-sm", onClick: handleComparison, title: "Porovnat zdroje titulk\u016F", children: [(0, jsx_runtime_1.jsx)(lucide_react_1.GitCompare, { className: "w-4 h-4" }), "Porovnat"] }))] })] }), error && ((0, jsx_runtime_1.jsxs)("div", { className: "error-message", children: [(0, jsx_runtime_1.jsx)(lucide_react_1.AlertCircle, { className: "w-4 h-4" }), (0, jsx_runtime_1.jsx)("span", { children: error })] })), (0, jsx_runtime_1.jsx)("div", { className: "sources-list", children: sources.length === 0 ? ((0, jsx_runtime_1.jsxs)("div", { className: "no-sources", children: [(0, jsx_runtime_1.jsx)(lucide_react_1.FileText, { className: "w-8 h-8 text-gray-400" }), (0, jsx_runtime_1.jsx)("p", { children: "\u017D\u00E1dn\u00E9 zdroje titulk\u016F" }), (0, jsx_runtime_1.jsx)("p", { className: "text-sm text-gray-500", children: "Za\u010Dn\u011Bte na\u010Dten\u00EDm titulk\u016F z YouTube nebo vytvo\u0159en\u00EDm Whisper transkripce" })] })) : (sources.map((source) => ((0, jsx_runtime_1.jsxs)("div", { className: `source-item ${selectedSourceId === source.source_id ? 'selected' : ''} ${source.status}`, children: [(0, jsx_runtime_1.jsxs)("div", { className: "source-info", children: [(0, jsx_runtime_1.jsxs)("div", { className: "source-header", children: [(0, jsx_runtime_1.jsx)("div", { className: "source-icon", children: getSourceIcon(source.source_type) }), (0, jsx_runtime_1.jsxs)("div", { className: "source-details", children: [(0, jsx_runtime_1.jsx)("h4", { children: source.source_name }), (0, jsx_runtime_1.jsxs)("div", { className: "source-meta", children: [(0, jsx_runtime_1.jsx)("span", { className: "language", children: source.language }), (0, jsx_runtime_1.jsxs)("span", { className: "segments", children: [source.total_segments, " segment\u016F"] }), (0, jsx_runtime_1.jsx)("span", { className: "created", children: new Date(source.created_at).toLocaleDateString('cs-CZ') })] })] })] }), (0, jsx_runtime_1.jsxs)("div", { className: "source-status", children: [getStatusIcon(source.status), (0, jsx_runtime_1.jsx)("span", { children: getStatusText(source.status) })] })] }), (0, jsx_runtime_1.jsxs)("div", { className: "source-actions", children: [source.status === 'completed' && ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)("button", { className: "btn btn-sm btn-ghost", onClick: () => handleSourceSelect(source), title: "Zobrazit v editoru", children: (0, jsx_runtime_1.jsx)(lucide_react_1.Play, { className: "w-4 h-4" }) }), (0, jsx_runtime_1.jsx)("button", { className: "btn btn-sm btn-ghost", onClick: () => { }, title: "N\u00E1hled", children: (0, jsx_runtime_1.jsx)(lucide_react_1.Eye, { className: "w-4 h-4" }) })] })), (0, jsx_runtime_1.jsx)("button", { className: "btn btn-sm btn-ghost text-red-500", onClick: () => deleteSource(source.source_id), title: "Smazat zdroj", children: (0, jsx_runtime_1.jsx)(lucide_react_1.Trash2, { className: "w-4 h-4" }) })] })] }, source.source_id)))) })] }));
};
exports.default = SubtitleSourceManager;
